import { computed, ref, watch } from 'vue'
import { useCart } from '@/composables/useCart'

const STORAGE_KEY = 'portal.mall.cartSelected'

function load(): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

const selectedKeys = ref<string[]>(load())
let bootstrapped = false

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedKeys.value))
  } catch {
    /* ignore */
  }
}

export function useCartSelection() {
  const { lines } = useCart()

  watch(
    lines,
    (next) => {
      const alive = new Set(next.map((line) => line.key))
      let nextSel = selectedKeys.value.filter((key) => alive.has(key))

      if (!bootstrapped) {
        bootstrapped = true
        if (!nextSel.length && next.length) {
          nextSel = next
            .filter((line) => !line.soldOut && !line.stockShort)
            .map((line) => line.key)
        }
      }

      if (
        nextSel.length !== selectedKeys.value.length ||
        nextSel.some((key, index) => key !== selectedKeys.value[index])
      ) {
        selectedKeys.value = nextSel
        persist()
      }
    },
    { deep: true, immediate: true },
  )

  const selectedSet = computed(() => new Set(selectedKeys.value))
  const selectedLines = computed(() => lines.value.filter((line) => selectedSet.value.has(line.key)))
  const allSelected = computed(
    () => lines.value.length > 0 && lines.value.every((line) => selectedSet.value.has(line.key)),
  )

  function isSelected(key: string) {
    return selectedSet.value.has(key)
  }

  function toggle(key: string) {
    if (selectedSet.value.has(key)) {
      selectedKeys.value = selectedKeys.value.filter((item) => item !== key)
    } else {
      selectedKeys.value = [...selectedKeys.value, key]
    }
    persist()
  }

  function selectAll(on: boolean) {
    selectedKeys.value = on ? lines.value.map((line) => line.key) : []
    persist()
  }

  function clearSelection() {
    selectedKeys.value = []
    persist()
  }

  /** 바로구매 등 — 지정한 줄만 결제 대상으로 남긴다 */
  function selectOnly(keys: string[]) {
    const alive = new Set(lines.value.map((line) => line.key))
    selectedKeys.value = keys.filter((key) => alive.has(key))
    persist()
  }

  return {
    selectedKeys,
    selectedLines,
    allSelected,
    isSelected,
    toggle,
    selectAll,
    clearSelection,
    selectOnly,
  }
}
